import React, { useEffect, useMemo, useState } from "react";
import Text from "@/components/ui/text";
import { ChevronRight } from "lucide-react";
import { useTranslation } from "react-i18next";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { getEntityTypeFromPageName } from "@/components/layouts/Activities/Activities";
import { useParams } from "react-router-dom";
import CompanyCommercialGroup from "@/pages/companies/components/CompanyCommercials/CompanyCommercialGroup";
import { useProducts } from "@/entities/settings/hooks";
import { Commercials } from "@/pages/deals/types";
import { ChevronDown, Groups } from "@dreamhub/designsystem/icons";
import { cn } from "@/utils/utils";
import Dropdown from "@/components/controls/Dropdown/Dropdown";
import IconContainer from "@/components/ui/icon-container";
import { useCompanyPastContracts } from "@/pages/companies/hooks";
import {
  EntityType,
  getEntityHook
} from "@/services/hooks/entities/useEntityType";
import { keys, groupBy as lodashGroupBy, map } from "lodash";

const TRANSLATION_PREFIX = "companies.commercials.pastContractsPage";
export const GROUP_BY_PRODUCT = "product";
export const GROUP_BY_CONTRACT_EXPIRATION = "expirationDate";

const PastContracts: React.FC = () => {
  const { t } = useTranslation();
  const params = useParams();
  const location = useLocation();
  const navigate = useNavigate();
  const pageName = location.pathname.split("/")[1];
  const idParam = Object.entries(params).find(([key]) =>
    key.toLowerCase().includes("id")
  );
  const entityId = idParam ? idParam[1] : undefined;
  const entityType = getEntityTypeFromPageName(pageName) ?? EntityType.DEAL;
  const useEntityQuery = getEntityHook(entityType);
  const entityQuery = useEntityQuery
    ? useEntityQuery(entityId ? [entityId] : [])
    : { entities: [], isSuccess: false };

  const { entities: [entityData] = [] } = entityQuery;

  const products = useProducts()?.data?.products;

  const isMultiProduct = products && products?.length > 1;

  const groupByOptions = [GROUP_BY_PRODUCT, GROUP_BY_CONTRACT_EXPIRATION];

  const [groupBy, setGroupBy] = useState<string[]>([GROUP_BY_PRODUCT]);
  const [isGroupByMenuOpen, setIsGroupByMenuOpen] = useState(false);
  const [groupedContractsData, setGroupedContractsData] = useState<{
    [key: string]: Commercials[];
  }>({});

  const companyPastContracts = useCompanyPastContracts(
    entityData?.id || "",
    groupBy[0]
  );

  useEffect(() => {
    const groupedData = lodashGroupBy(
      companyPastContracts?.data,
      groupBy[0] === GROUP_BY_PRODUCT
        ? "productId"
        : groupBy[0] === GROUP_BY_CONTRACT_EXPIRATION
          ? "endYear"
          : ""
    );
    setGroupedContractsData(groupedData);
  }, [companyPastContracts?.data, groupBy]);

  const groupByOptionsMap = useMemo(() => {
    return Object.fromEntries(
      groupByOptions.map((key) => [
        key,
        t(`${TRANSLATION_PREFIX}.groupByOptions.${key}`)
      ])
    );
  }, [groupByOptions, t]);

  return (
    <div className="flex flex-col">
      <div className="w-full flex flex-col gap-4 border-b border-border-border2">
        <div className="w-full flex justify-between pl-6 pr-4 min-h-12 pt-4">
          <div className="flex gap-2 items-center">
            <Link to={`/${pageName}`}>
              <Text variant="lgSubtitleH3">{t(`pages.${pageName}`)}</Text>
            </Link>
            <ChevronRight className="size-5 text-secondary" />
            <Text
              variant="lgSubtitleH3"
              className="cursor-pointer"
              onClick={() => navigate(-1)}
            >
              {entityData?.name}
            </Text>
            <ChevronRight className="size-5 text-secondary" />
            <Text variant="lgSubtitleH1">
              {t(`${TRANSLATION_PREFIX}.title`)}
            </Text>
          </div>
        </div>
        {isMultiProduct && (
          <div className="pr-2 pb-2 flex justify-end">
            <div
              className={cn(
                "flex items-center !pl-[6px] !py-1 !pr-1 gap-1.5 border rounded transition-all duration-200",
                isGroupByMenuOpen
                  ? "border-primary"
                  : "border-transparent hover:bg-white-8"
              )}
            >
              <IconContainer as="span" size="18" color="secondary" nativeStroke>
                <Groups />
              </IconContainer>
              <Dropdown
                options={groupByOptionsMap}
                defaultValue={groupBy[0] || GROUP_BY_PRODUCT}
                valueSelected={(value) => {
                  setGroupBy([value]);
                }}
                label={t("datatoolbar.groupBy.title")}
                placeholder={t("datatoolbar.groupBy.title")}
                value={groupBy[0] || GROUP_BY_PRODUCT}
                className="min-w-fit text-nowrap gap-[6px] p-1"
                containerClassName="gap-x-2"
                onOpenChange={setIsGroupByMenuOpen}
                sideOffset={7}
                align="end"
                hideBorder
                customIcon={
                  <IconContainer size="5" color="secondary">
                    <ChevronDown />
                  </IconContainer>
                }
              />
            </div>
          </div>
        )}
      </div>
      <div className="flex flex-col gap-10 p-6 w-full">
        {map(keys(groupedContractsData), (group, index) => (
          <CompanyCommercialGroup
            key={group + index}
            group={group}
            commercials={groupedContractsData[group]}
            groupedBy={groupBy}
            products={products}
            isMultiProduct={isMultiProduct}
          />
        ))}
      </div>
    </div>
  );
};

export default PastContracts;
